import {
  ARRAY_FUNCTIONS,
  BOOLEAN_FUNCTIONS,
  DATE_FUNCTIONS,
  LEX_TOKEN_TYPES,
  LOGIC_FUNCTIONS,
  OPERATORS,
  REGEXP,
  STRING_FUNCTIONS,
  SWITCH_FUNCTIONS,
  TO_STRING_FUNCTION,
} from 'constants/parser';

export const languageId = 'expression';
export const themeId = 'expressionTheme';
export const themeIdDark = 'expressionThemeDark';

export const expressionThemeConfig = {
  base: 'vs',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: '7b2ff7' },
    { token: 'keyword.logic', foreground: '7b2ff7', fontStyle: 'bold' },
    { token: LEX_TOKEN_TYPES.field, foreground: '3949ab' },
    { token: 'field.leaf', foreground: '3949ab', fontStyle: 'bold' },
    { token: 'identifier', foreground: '3949ab' },
    { token: 'iterator', foreground: '0c8599', fontStyle: 'italic' },
    { token: LEX_TOKEN_TYPES.operator, foreground: '5b6573' },
    { token: 'string', foreground: 'e07b00' },
    { token: 'string.quote', foreground: 'e07b00' },
    { token: 'number', foreground: '1565c0' },
    { token: 'number.double', foreground: '1565c0' },
    { token: 'number.integer', foreground: '1565c0' },
    { token: 'date', foreground: 'c62828' },
    { token: 'constant.boolean', foreground: '2e7d32' },
    { token: 'delimiter.parenthesis', foreground: '9aa4b1' },
  ],
  colors: {},
};

// Monaco does NOT inherit the MUI/CDS palette — it owns its own theme registry, so dark mode needs
// an explicit dark theme. Token hues are lightened versions of the light theme above so they stay
// legible on the dark editor surface; the colors map aligns the editor chrome with the app's dark
// panel/input surface (DARK_PANEL2 / DARK_BG in themesConfig).
export const expressionThemeConfigDark = {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: 'c4a7ff' },
    { token: 'keyword.logic', foreground: 'c4a7ff', fontStyle: 'bold' },
    { token: LEX_TOKEN_TYPES.field, foreground: '9fb4ff' },
    { token: 'field.leaf', foreground: '9fb4ff', fontStyle: 'bold' },
    { token: 'identifier', foreground: '9fb4ff' },
    { token: 'iterator', foreground: '5fd3e3', fontStyle: 'italic' },
    { token: LEX_TOKEN_TYPES.operator, foreground: 'aab4c2' },
    { token: 'string', foreground: 'f0a868' },
    { token: 'string.quote', foreground: 'f0a868' },
    { token: 'number', foreground: '6cb6ff' },
    { token: 'number.double', foreground: '6cb6ff' },
    { token: 'number.integer', foreground: '6cb6ff' },
    { token: 'date', foreground: 'f08a8a' },
    { token: 'constant.boolean', foreground: '7ee787' },
    { token: 'delimiter.parenthesis', foreground: '8a94a3' },
  ],
  colors: {
    'editor.background': '#1C2433',
    'editor.foreground': '#E6EAF0',
    'editorGutter.background': '#1C2433',
    'editorLineNumber.foreground': '#5A6678',
    'editorLineNumber.activeForeground': '#9FB0C8',
    'editor.lineHighlightBackground': '#222C3D',
    'editor.lineHighlightBorder': '#00000000',
    'editorCursor.foreground': '#3D8BFF',
    'editor.selectionBackground': '#2C3D5A',
    'editorWidget.background': '#1C2433',
    'editorWidget.border': '#2A3344',
    'editorIndentGuide.background': '#2A3344',
    'editorIndentGuide.activeBackground': '#3A4658',
  },
};

export const getEditorConstructionOptions = ({ readOnly }) => ({
  readOnly: readOnly,
  domReadOnly: readOnly,

  automaticLayout: true,
  bracketPairColorization: false,
  fixedOverflowWidgets: true,
  folding: false,
  lineNumbers: 'on',
  lineNumbersMinChars: 2,
  lineDecorationsWidth: 6,
  matchBrackets: 'always',
  multiCursorLimit: 1,
  placeholder: 'Expression',
  scrollBeyondLastColumn: 0,
  scrollBeyondLastLine: 0,
  wordWrap: 'on',

  // Native suggest widget is replaced by the custom AutocompletePopover.
  quickSuggestions: false,
  suggestOnTriggerCharacters: false,
  acceptSuggestionOnEnter: 'off',
  tabCompletion: 'off',
  wordBasedSuggestions: 'off',

  minimap: {
    enabled: false,
  },
  padding: {
    top: 10,
    bottom: 10,
  },
  suggest: {
    showStatusBar: false,
    showWords: false,
  },
});

export const expressionMonarchDefinition = {
  // set defaultToken to invalid to see what you do not tokenize yet
  // defaultToken: 'invalid',

  brackets: [['(', ')', 'delimiter.parenthesis']],

  keywords: [
    ...BOOLEAN_FUNCTIONS,
    ...STRING_FUNCTIONS,
    ...ARRAY_FUNCTIONS,
    ...DATE_FUNCTIONS,
    ...SWITCH_FUNCTIONS,
    TO_STRING_FUNCTION,
    'PERCENT',
  ],

  logicKeywords: [...LOGIC_FUNCTIONS],

  operators: [...OPERATORS, '><'],

  booleans: ['true', 'false'],

  tokenizer: {
    root: [
      [/_[a-zA-Z]\w*/, 'iterator'],
      [/((?:[a-zA-Z_]\w*\.)+)([a-zA-Z_]\w*)/, ['field', 'field.leaf']],
      [
        /[a-zA-Z_][\w$]*/,
        {
          cases: {
            '@booleans': 'constant.boolean',
            '@logicKeywords': 'keyword.logic',
            '@keywords': 'keyword',
            '@default': 'identifier',
          },
        },
      ],

      [REGEXP.operator, { cases: { '@operators': 'operator', '@default': '' } }],

      [REGEXP.date, 'date'],

      [REGEXP.double, 'number.double'],
      [REGEXP.integer, 'number.integer'],
      [/\d+/, 'number'],

      [/'/, { token: 'string.quote', bracket: '@open', next: '@string' }],

      [REGEXP.bracket, '@brackets'],

      { include: '@whitespace' },

      [REGEXP.field, 'field'],
      [REGEXP.anySingleCharacter, 'character'],
    ],

    string: [
      [/[^\\']+/, 'string'],
      [/'/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
    ],

    whitespace: [[REGEXP.space, 'white']],
  },
};

export const expressionLanguageConfig = {
  surroundingPairs: [{ open: '(', close: ')' }],
  autoClosingPairs: [{ open: '(', close: ')' }],
  brackets: [['(', ')']],
};
